/**
 * Pure helpers that turn a confirmed reservation into calendar artifacts:
 * a downloadable .ics file (RFC 5545) and a Google Calendar "add event" URL.
 *
 * No React, no DOM access — these are deterministic and safe to unit test or
 * import anywhere. The consuming component is responsible for the Blob download
 * and analytics; these functions only build strings.
 */

const LAS_VEGAS_TIME_ZONE = "America/Los_Angeles";

export interface ReservationCalendarEvent {
  title: string;
  description: string;
  location: string;
  /** UTC instant for the reservation start, e.g. from lasVegasWallClockToUtcIso(). */
  startIso: string;
  durationMinutes: number;
  /** PRODID product name — the host restaurant, never a hardcoded tenant. */
  calendarProduct: string;
  /** Domain used in the VEVENT UID, e.g. tuscancove.com. */
  calendarUidDomain: string;
}

export interface ReservationCalendarVenue {
  name: string;
  phone: string;
  address: {
    line1: string;
    city: string;
    state: string;
    zip: string;
  };
  domain: string;
  restaurantId: string;
}

export interface ReservationCalendarArtifacts {
  event: ReservationCalendarEvent;
  icsFileName: string;
  phoneDisplay: string;
  phoneHref: string;
}

/**
 * Resolve the UTC instant for a wall-clock time in Las Vegas
 * (America/Los_Angeles) and return it as an ISO string.
 *
 * The reservation date + time the guest picked is a local Las Vegas wall clock.
 * We read the zone's real offset at that moment via Intl rather than assuming a
 * fixed -8/-7, so the result stays correct across the PST/PDT switch.
 *
 * @param date - calendar date as YYYY-MM-DD
 * @param time - 24h wall-clock time as HH:MM
 */
export function lasVegasWallClockToUtcIso(date: string, time: string): string {
  const [year, month, day] = date.split("-").map(Number);
  const [hour, minute] = time.split(":").map(Number);

  // Treat the wall clock as if it were already UTC, then subtract the zone's
  // actual offset at that instant to land on the true UTC moment.
  const wallClockAsUtc = Date.UTC(year, month - 1, day, hour, minute);
  let offsetMs = getTimeZoneOffsetMs(LAS_VEGAS_TIME_ZONE, wallClockAsUtc);
  // Refine once in case the first guess landed on the wrong side of a DST
  // boundary (the offset there differs from the offset at the real instant).
  offsetMs = getTimeZoneOffsetMs(LAS_VEGAS_TIME_ZONE, wallClockAsUtc - offsetMs);

  return new Date(wallClockAsUtc - offsetMs).toISOString();
}

/**
 * Offset (in ms) between a named time zone's wall clock and UTC at a given
 * instant. Positive east of UTC, negative west (Las Vegas is negative).
 */
function getTimeZoneOffsetMs(timeZone: string, utcMs: number): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(new Date(utcMs));

  const lookup: Record<string, number> = {};
  for (const part of parts) {
    if (part.type !== "literal") lookup[part.type] = Number(part.value);
  }

  // Intl can render midnight as hour "24"; normalize it to 0.
  const hour = lookup.hour === 24 ? 0 : lookup.hour;
  const wallClockAsUtc = Date.UTC(
    lookup.year,
    lookup.month - 1,
    lookup.day,
    hour,
    lookup.minute,
    lookup.second
  );

  return wallClockAsUtc - utcMs;
}

/** Compact UTC stamp the calendar formats expect, e.g. 20260610T020000Z. */
function toCalendarUtcStamp(iso: string): string {
  return new Date(iso).toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
}

function eventBounds(event: ReservationCalendarEvent): { start: string; end: string } {
  const startMs = new Date(event.startIso).getTime();
  const endMs = startMs + event.durationMinutes * 60 * 1000;
  return {
    start: toCalendarUtcStamp(new Date(startMs).toISOString()),
    end: toCalendarUtcStamp(new Date(endMs).toISOString()),
  };
}

/** Escape a value for an RFC 5545 TEXT field (backslash, comma, semicolon, newline). */
function escapeIcsText(value: string): string {
  return value
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\r\n|\r|\n/g, "\\n");
}

/**
 * Fold a content line to 75 octets per RFC 5545 §3.1. Continuation lines begin
 * with a single space. Iterates by code point so multi-byte characters are
 * never split mid-sequence.
 */
function foldIcsLine(line: string): string {
  const encoder = new TextEncoder();
  if (encoder.encode(line).length <= 75) return line;

  const segments: string[] = [];
  let current = "";
  let currentBytes = 0;

  for (const char of line) {
    const charBytes = encoder.encode(char).length;
    // First line allows 75 octets; continuation lines reserve 1 for the leading space.
    const limit = segments.length === 0 ? 75 : 74;
    if (currentBytes + charBytes > limit) {
      segments.push(current);
      current = "";
      currentBytes = 0;
    }
    current += char;
    currentBytes += charBytes;
  }
  if (current) segments.push(current);

  return segments.join("\r\n ");
}

/**
 * Deterministic 32-bit string hash (djb2-ish) rendered base36, used to give the
 * VEVENT a stable UID without pulling in randomness or wall-clock time.
 */
function hashString(value: string): string {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = (hash << 5) - hash + value.charCodeAt(i);
    hash |= 0; // keep it 32-bit
  }
  return (hash >>> 0).toString(36);
}

/**
 * Build a valid RFC 5545 VCALENDAR string for a single reservation VEVENT.
 * Times are emitted in UTC ("Z") form. DTSTAMP is derived from the start so the
 * output is fully deterministic (no Date.now()), which keeps it testable.
 * Lines use CRLF endings and are folded at 75 octets.
 */
export function formatReservationPhoneDisplay(phone: string): string {
  const digits = phone.replace(/\D/g, "");
  const normalized =
    digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;

  if (normalized.length !== 10) {
    return phone;
  }

  return `(${normalized.slice(0, 3)}) ${normalized.slice(3, 6)}-${normalized.slice(6)}`;
}

export function formatReservationAddress(address: ReservationCalendarVenue["address"]): string {
  return `${address.line1}, ${address.city}, ${address.state} ${address.zip}`;
}

export function buildReservationIcsFileName(restaurantId: string): string {
  const slug = restaurantId.trim().toLowerCase();
  if (!slug || !/^[a-z0-9-]+$/.test(slug)) {
    throw new Error("Unknown reservation calendar restaurant");
  }
  return `${slug}-reservation.ics`;
}

function requireCalendarIdentity(value: string, label: string): string {
  const trimmed = value.trim();
  if (!trimmed) {
    throw new Error(`Unknown reservation calendar ${label}`);
  }
  return trimmed;
}

export function buildReservationCalendarArtifacts(args: {
  venue: ReservationCalendarVenue;
  confirmationCode: string;
  partySize: number;
  customerName: string;
  date: string;
  time: string;
  durationMinutes?: number;
}): ReservationCalendarArtifacts {
  const name = requireCalendarIdentity(args.venue.name, "name");
  const domain = requireCalendarIdentity(args.venue.domain, "domain");
  const phoneDisplay = formatReservationPhoneDisplay(
    requireCalendarIdentity(args.venue.phone, "phone"),
  );
  const location = formatReservationAddress(args.venue.address);
  if (!args.venue.address.line1.trim()) {
    throw new Error("Unknown reservation calendar address");
  }

  return {
    event: {
      title: `Table for ${args.partySize} at ${name}`,
      description: [
        `Reservation for ${args.partySize} ${args.partySize === 1 ? "guest" : "guests"}`,
        `Confirmation code: ${args.confirmationCode}`,
        `Name: ${args.customerName}`,
        "",
        `Need to change or cancel? Call ${phoneDisplay}`,
      ].join("\n"),
      location,
      startIso: lasVegasWallClockToUtcIso(args.date, args.time),
      durationMinutes: args.durationMinutes ?? 90,
      calendarProduct: name,
      calendarUidDomain: domain,
    },
    icsFileName: buildReservationIcsFileName(args.venue.restaurantId),
    phoneDisplay,
    phoneHref: args.venue.phone,
  };
}

export function buildReservationIcs(event: ReservationCalendarEvent): string {
  const { start, end } = eventBounds(event);
  const calendarProduct = requireCalendarIdentity(
    event.calendarProduct,
    "product",
  );
  const calendarUidDomain = requireCalendarIdentity(
    event.calendarUidDomain,
    "domain",
  );
  const uid = `${start}-${hashString(event.title + event.location + event.description)}@${calendarUidDomain}`;

  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    `PRODID:-//${escapeIcsText(calendarProduct)}//Reservations//EN`,
    "CALSCALE:GREGORIAN",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${start}`,
    `DTSTART:${start}`,
    `DTEND:${end}`,
    `SUMMARY:${escapeIcsText(event.title)}`,
    `DESCRIPTION:${escapeIcsText(event.description)}`,
    `LOCATION:${escapeIcsText(event.location)}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ];

  return lines.map(foldIcsLine).join("\r\n") + "\r\n";
}

/**
 * Build a Google Calendar "create event" URL. Times use the same compact UTC
 * stamp the .ics uses; URLSearchParams handles encoding of text/details/location.
 */
export function buildGoogleCalendarUrl(event: ReservationCalendarEvent): string {
  const { start, end } = eventBounds(event);

  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: event.title,
    dates: `${start}/${end}`,
    details: event.description,
    location: event.location,
  });

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}
